import { useEffect, useState } from "react"
import { Loader2, Check, Pause, Minus } from "lucide-react"
import type { BacktestStatus } from "../types/backtest"
import { cn } from "@/lib/utils"

export interface BacktestStatusPillProps {
  status: BacktestStatus
  startedAt?: number | null
  elapsed?: number
  className?: string
}

export function BacktestStatusPill({
  status,
  startedAt,
  elapsed = 0,
  className,
}: BacktestStatusPillProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (status !== "running" && status !== "replaying") return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 100)
    return () => clearInterval(id)
  }, [status])

  const liveElapsed =
    (status === "running" || status === "replaying") && startedAt
      ? (elapsed + Math.max(0, (now - startedAt) / 1e3)).toFixed(1)
      : elapsed.toFixed(1)

  if (status === "starting") {
    return (
      <div className={cn("flex items-center gap-2 shrink-0", className)}>
        <Loader2 size={12} strokeWidth={2} className="text-primary animate-spin shrink-0" />
        <span className="text-caption text-foreground whitespace-nowrap">Backtest starting…</span>
      </div>
    )
  }

  if (status === "running") {
    return (
      <div className={cn("flex items-center gap-2 shrink-0", className)}>
        <Loader2 size={12} strokeWidth={2} className="text-primary animate-spin shrink-0" />
        <span className="text-caption text-foreground whitespace-nowrap">Backtest running…</span>
        <span className="text-caption text-muted-foreground tabular-nums whitespace-nowrap">
          {liveElapsed}s
        </span>
      </div>
    )
  }

  if (status === "replaying") {
    return (
      <div className={cn("flex items-center gap-2 shrink-0", className)}>
        <Loader2 size={12} strokeWidth={2} className="text-primary animate-spin shrink-0" />
        <span className="text-caption text-foreground whitespace-nowrap">
          <span className="min-[1100px]:hidden">Backtesting…</span>
          <span className="hidden min-[1100px]:inline">Playing backtest…</span>
        </span>
        <span className="text-caption text-muted-foreground tabular-nums whitespace-nowrap">
          {liveElapsed}s
        </span>
      </div>
    )
  }

  if (status === "paused") {
    return (
      <div className={cn("flex items-center gap-2 shrink-0", className)}>
        <Pause size={12} strokeWidth={2} className="text-muted-foreground shrink-0" />
        <span className="text-caption text-muted-foreground whitespace-nowrap">Play paused</span>
        <span className="text-caption text-muted-foreground tabular-nums whitespace-nowrap">
          {liveElapsed}s
        </span>
      </div>
    )
  }

  if (status === "complete") {
    return (
      <div className={cn("flex items-center gap-1.5 shrink-0", className)}>
        <Check size={12} strokeWidth={2.5} className="text-foreground shrink-0" />
        <span className="text-caption text-foreground whitespace-nowrap">Backtest complete</span>
      </div>
    )
  }

  if (status === "canceled") {
    return (
      <div className={cn("flex items-center gap-1.5 shrink-0", className)}>
        <Minus size={12} strokeWidth={2} className="text-muted-foreground shrink-0" />
        <span className="text-caption text-muted-foreground whitespace-nowrap">Backtest aborted</span>
      </div>
    )
  }

  return null
}
